<template>
  <section class="ui-card rcp-card" :class="{ 'is-closed': closed, 'is-edited': c.edited > 0, 'is-none': !c.hasRecipe }" :data-meal="c.name">
    <div class="rcp-card__head">
      <button type="button" class="rcp-card__title" :aria-expanded="String(!closed)" @click="ctl.toggleCard(c.name)">
        <v-icon size="18">mdi-chevron-down</v-icon>
        <b><recipe-hit :text="toLatin(c.name)" :query="query"></recipe-hit></b>
      </button>
      <button
        v-if="c.edited"
        type="button"
        class="rcp-card__edited"
        :title="$t('recipes_shell.revert_meal_hint')"
        @click="ctl.revertMeal(c.name)"
      >
        <v-icon size="14">mdi-undo-variant</v-icon>
        {{ $t("recipes_shell.edited") }}
      </button>
      <span class="rcp-card__count rcp-num">
        <b>{{ ctl.fq(c.portions) }}</b>
        {{ ctl.pluralT("recipes_shell.portion_word", c.portions) }}
      </span>
    </div>

    <template v-if="!closed">
      <div v-if="!c.hasRecipe" class="rcp-none">
        <span class="rcp-ring"><v-icon size="20">mdi-book-open-page-variant-outline</v-icon></span>
        <div>
          <b>{{ $t("recipes_shell.no_recipe") }}</b>
          <p>{{ $t("recipes_shell.no_recipe_text") }}</p>
          <button v-if="ctl.canAddRecipe" type="button" class="ui-btn ui-btn--sm" @click="ctl.addRecipe">
            {{ $t("recipes_shell.add_recipe") }}
            <v-icon size="14">mdi-open-in-new</v-icon>
          </button>
        </div>
      </div>

      <div v-else class="rcp-wide">
        <table class="ui-table ui-table--dense">
          <thead>
            <tr>
              <th scope="col"><span class="ui-sr">{{ $t("grocery") }}</span></th>
              <th v-for="col in c.cols" :key="col.shift" scope="col"><report-shift-tag :shift="col.shift"></report-shift-tag></th>
              <th scope="col">{{ $t("total") }}</th>
              <th v-if="ctl.price" scope="col">{{ $t("recipes_shell.cost") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr class="rcp-portions">
              <th scope="row">{{ $t("recipes_shell.portions") }}</th>
              <td v-for="col in c.cols" :key="col.shift">
                <portion-field
                  :value="col.count"
                  :edited="col.edited"
                  :shift="col.shift"
                  :label="$t('recipes_shell.portions_for', { meal: toLatin(c.name), shift: col.shift })"
                  :title="$t('recipes_shell.ordered', { n: col.base })"
                  @input="ctl.editPortions(c.name, col.shift, $event)"
                ></portion-field>
              </td>
              <td class="rcp-total">
                <b>{{ ctl.fq(c.portions) }}</b>
                <i v-if="delta" class="rcp-delta">{{ ctl.sign(delta) }}</i>
              </td>
              <td v-if="ctl.price"></td>
            </tr>
            <tr v-for="(row, index) in rows" :key="`${row.id}-${index}`">
              <th scope="row" class="rcp-name"><recipe-hit :text="toLatin(row.name)" :query="query"></recipe-hit></th>
              <td v-for="(value, i) in row.shown" :key="c.cols[i].shift" :class="{ 'is-zero': !value }">
                {{ value ? ctl.fq(value) : "—" }}
              </td>
              <td class="rcp-total">
                <b>{{ ctl.fq(row.total) }}</b>
                <span class="rcp-unit">&nbsp;{{ row.measure }}</span>
              </td>
              <td v-if="ctl.price">{{ row.cost == null ? "—" : ctl.fm(row.cost) }}</td>
            </tr>
          </tbody>
          <tfoot v-if="ctl.price">
            <tr>
              <th scope="row">{{ $t("recipes_shell.meal_cost") }}</th>
              <td :colspan="c.cols.length + 1" class="rcp-per">{{ perPortion }}</td>
              <td>
                <b>{{ money(c.cost) }}</b>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </template>
  </section>
</template>

<script>
import ReportShiftTag from "@/components/report/shared/ReportShiftTag.vue";
import PortionField from "./PortionField.vue";
import RecipeHit from "./RecipeHit.vue";

/**
 * Kartica jednog jela: naziv i broj porcija, red "Porcije" sa poljima po smjenama (izmjena važi samo na ekranu, u štampi
 * i u Excelu) i namirnice sa količinom po smjenama, ukupno i (kad je uključeno) troškom. Sklapa se klikom na naziv.
 * Jelo bez recepta pokazuje objašnjenje umjesto tabele.
 */
export default {
  name: "RecipeCard",
  components: { ReportShiftTag, PortionField, RecipeHit },
  inject: { ctl: "recipesCtl" },
  props: {
    /** Izračun jela (mealCalc) */
    c: { type: Object, required: true },
    /** Namirnice koje se prikazuju (pretraga može sakriti dio) */
    rows: { type: Array, required: true },
    query: { type: String, default: "" },
  },
  computed: {
    closed() {
      return this.ctl.isClosed(this.c.name);
    },
    delta() {
      return this.c.cols.reduce((total, col) => total + col.delta, 0);
    },
    perPortion() {
      return this.$t("recipes_shell.per_portion_value", { value: this.money(this.c.perPortion) });
    },
  },
  methods: {
    money(value) {
      return [this.ctl.fm(value), this.ctl.currency].filter(Boolean).join(" ");
    },
  },
};
</script>
